import { useSearchParams } from "react-router-dom";
import { useChapterOutroContent } from "@/hooks/course/useChapterOutroContent";
import ChapterOutroScroll from "@/components/course/ChapterOutroScroll";
import { SectionContent } from "@/components/course/SectionContent";
import { CHAPTER_OUTRO_FALLBACK } from "@/components/course/introFallbacks";
import {
  OUTRO_ENTRY_MODE,
  type OutroEntryMode,
} from "@/hooks/course/useChapterOutroReveal";
import { isUnveilQuery } from "@/lib/chapterNavigation";
import type { CourseTreeNode } from "@/types/generated";

interface ChapterOutroPageProps {
  chapter: CourseTreeNode;
}

/**
 * Chapter outro route view — stacked last-subchapter + Abschluss when content exists.
 */
const ChapterOutroPage = ({ chapter }: ChapterOutroPageProps) => {
  const [searchParams] = useSearchParams();
  const outroProps = useChapterOutroContent(chapter, CHAPTER_OUTRO_FALLBACK);
  // `unveil=1` = last-sub content entry; bare `view=outro` = TOC Abschluss.
  const initialMode: OutroEntryMode = isUnveilQuery(searchParams)
    ? OUTRO_ENTRY_MODE.Content
    : OUTRO_ENTRY_MODE.Outro;

  if (!outroProps.lastSubchapter) {
    return (
      <div className="bg-background px-10 pt-8">
        <SectionContent
          title={outroProps.title}
          body={outroProps.body}
          ctaLabel={outroProps.ctaLabel}
          onStart={outroProps.onNext}
        />
      </div>
    );
  }

  return (
    <ChapterOutroScroll
      key={initialMode}
      initialMode={initialMode}
      title={outroProps.title}
      body={outroProps.body}
      ctaLabel={outroProps.ctaLabel}
      onNext={outroProps.onNext}
      onOutroSeen={outroProps.onOutroSeen}
      contentTitle={outroProps.contentTitle}
      contentElements={outroProps.contentElements}
      storyboards={outroProps.storyboards}
      storyboardsLoading={outroProps.storyboardsLoading}
      glossaryTerms={outroProps.glossaryTerms}
    />
  );
};

export { ChapterOutroPage };
